import React, { useEffect, useState, useMemo, useCallback } from 'react';
import { useLocation, useHistory } from 'react-router-dom';
import qs from 'query-string';
import { format, parseISO } from 'date-fns';
import { Button, Row, Col, Container } from 'react-bootstrap';
import { FaClock, FaPlay, FaStop } from 'react-icons/fa';
import { useRouteMatch } from 'react-router';
import { isNull } from 'lodash';
import Loading from '../../components/Loading';
import api from '../../services/api';
import { formataCPF, formataIdade, formataTelefone } from '../../utils/funcoes';

import { Titulo } from './styles';

import { useToast } from '../../hooks/toast';
import { useClinica } from '../../hooks/clinica';
import PacienteCabecalho from '../../components/PacienteCabecalho';
import { IProcedimentoAgendamento } from '../../components/Agendamento';
import { IExamesAlvaro } from '../Exame';
import { useAuth } from '../../hooks/auth';
import { IPacienteCodigo } from '../PacienteAtendimento';
import getResponseErrors from '../../utils/getResponseErrors';

interface IUrlParams {
  id: string;
}

interface ICreateExame {
  ExaAgeCodigo: number;
  ExaPacCodigo: number;
  ExaPacNome: string;
  ExaPacNomeSocial: string;
  ExaPacSexo: string;
  ExaData: string;
  ExaPacDataNasc: Date;
  ExaUsuCodigo: number;
  ExaCliCodigo: number;
  ExaMedNome?: string;
  ExaMedCRM?: string;
  ExaMedUF?: string;
  exameItens: {
    ItmExaCodigo: string;
    ItmMaterial: string;
    ItmExaQtde: number;
  }[];
}
interface IExame {
  id?: number;
  ExaAgeCodigo: number;
  ExaPacCodigo: number;
  ExaPacNome: string;
  ExaPacNomeSocial: string;
  ExaPacSexo: string;
  ExaData: string;
  ExaHora: string;
  ExaPacDataNasc: string;
  ExaCodigoLis: string;
  ExaCodOS: string;
  ExaObservacao: string;
  ExaIncluido: boolean;
  ExaBaseBarras: string;
  ExaUsuCodigo: number;
  ExaLink: string;
  ExaCliCodigo: string;
}

interface IMedicoSolicitante {
  MedNome: string;
  MedNumeroConselho: string;
  MedUF: string;
}

export interface IProcedimentoAgendamentoAlvaro
  extends IProcedimentoAgendamento {
  ProValidado?: boolean;
  ProCodigoAlvaro?: string;
  exameAlvaro?: IExamesAlvaro;
}

const PacienteAtendimentoAlvaro: React.FC = () => {
  const { user } = useAuth();
  const location = useLocation();
  const history = useHistory();
  function useQuery(): URLSearchParams {
    return new URLSearchParams(location.search);
  }

  const query = useQuery();
  const { params } = useRouteMatch<IUrlParams>();
  const { id } = params;
  const { addToast } = useToast();
  const { imagensClinica } = useClinica();

  const semFoto =
    imagensClinica.find(img => img.nome === 'semFoto')?.arquivo_url || '';

  const [loading, setLoading] = useState(false);
  const [ageClinica, setAgeClinica] = useState<number | undefined>(undefined);
  const [paciente, setPaciente] = useState<IPacienteCodigo | undefined>(
    undefined,
  );
  const [
    medicoSolicitante,
    setMedicoSolicitante,
  ] = useState<IMedicoSolicitante>();
  const [procedimentos, setProcedimentos] = useState<
    IProcedimentoAgendamentoAlvaro[]
  >([]);

  const [exame, setExame] = useState<IExame[]>([]);

  const [codigoAgenda, setCodigoAgenda] = useState(() => {
    return query.get('agenda') || undefined;
  });
  const [dataAgenda, setDataAgenda] = useState(() => {
    return query.get('data') || undefined;
  });

  const [somenteLista, setSomenteLista] = useState(() => {
    return query.get('somenteLista') === 'true';
  });

  useEffect(() => {
    async function loadDados(): Promise<void> {
      setLoading(true);
      try {
        const response = await api.get(
          `/atendimentos/${id}/detalhe?ConIntegracao=1`,
        );

        if (response.data) {
          setAgeClinica(response.data.AgeClinica);
          setPaciente({
            ...response.data.paciente,
            PacCodigo: response.data.AgePacCodigo,
            PacTelefone: formataTelefone(response.data.paciente.PacTelefone),
            PacCelular: formataTelefone(response.data.paciente.PacCelular),
            PacCPF: formataCPF(response.data.paciente.PacCPF),
            PacDataNascFormatada: format(
              parseISO(response.data.paciente.PacDataNasc),
              'dd/MM/yyyy',
            ),
            PacIdade: formataIdade(
              parseISO(response.data.paciente.PacDataNasc),
            ),
            avatar_url: response.data.paciente.avatar_url || semFoto,
          });

          setProcedimentos(
            response.data.procedimentos.map(
              (pro: IProcedimentoAgendamentoAlvaro) => {
                return {
                  ...pro,
                  ProCodigo: pro.ProCodigo,
                  ProNome: pro.ProNome,
                  ProDuracao: pro.ProDuracao,
                  ProCodigoAlvaro:
                    pro.ProCodigoAlvaro && pro.ProCodigoAlvaro !== null
                      ? pro.ProCodigoAlvaro
                      : '---',
                  ProValidado:
                    pro.ProCodigoAlvaro !== '' &&
                    pro.ProCodigoAlvaro !== '---' &&
                    pro.ProCodigoAlvaro !== null &&
                    pro.exameAlvaro,
                  exameAlvaro: pro.exameAlvaro,
                };
              },
            ),
          );

          if (response.data.medicoSolicitante)
            setMedicoSolicitante({
              MedNome: response.data.medicoSolicitante.MedNome || '',
              MedNumeroConselho:
                response.data.medicoSolicitante.MedNumeroConselho || '',
              MedUF: response.data.medicoSolicitante.MedUF || '',
            });

          if (response.data.exames) {
            const exameRealizado = response.data.exames.map(
              (exa: IExame) => exa,
            );
            setExame(exameRealizado);
          }
          if (response.data.pacienteValidado !== '')
            addToast({
              title: 'Dados do Paciente inválidos',
              description: response.data.pacienteValidado,
              type: 'error',
            });

          if (response.data.exameInvalidos)
            addToast({
              title: 'Dados do exame(s) inválido(s)',
              description: response.data.exameInvalidos,
              type: 'error',
            });
        }
      } catch (error) {
        const mensagem = getResponseErrors(error);
        addToast({
          type: 'error',
          title: 'Não foi possível buscar o agendamento',
          description: mensagem,
        });
      } finally {
        setLoading(false);
      }
    }
    if (id && Number(id) !== 0) loadDados();
  }, [id, addToast, semFoto]);

  const handleVoltar = useCallback(() => {
    const queryParams = qs.parse(location.search);
    history.push({
      pathname: '../agendaatendimento',
      search: qs.stringify({
        ...queryParams,
        data: dataAgenda,
        agenda: codigoAgenda,
      }),
    });
  }, [codigoAgenda, dataAgenda, history, location.search]);

  const gerouOS = useMemo(() => {
    const exameID = exame[0] ? exame[0].id : undefined;
    const exameRealizado = exame?.find((exa: IExame) => !isNull(exa.ExaCodOS));
    if (!exameRealizado) return { gerou: false, CodExame: exameID };
    return {
      gerou: exameRealizado && exameRealizado.ExaCodOS !== '',
      CodExame: exameID,
    };
  }, [exame]);

  const handleCriarExame = useCallback(async () => {
    if (paciente && ageClinica) {
      if (gerouOS.CodExame) {
        history.push({
          pathname: '../exame',
          search: qs.stringify({
            id: gerouOS.CodExame,
            data: dataAgenda,
            agenda: codigoAgenda,
          }),
        });
      } else {
        try {
          const criaExame: ICreateExame = {
            ExaAgeCodigo: Number(id),
            ExaCliCodigo: ageClinica,
            ExaData: format(new Date(), 'yyyy-MM-dd HH:mm:ss'),
            ExaPacCodigo: paciente.PacCodigo,
            ExaPacDataNasc: parseISO(paciente.PacDataNasc),
            ExaPacNome: paciente.PacNome,
            ExaPacNomeSocial: paciente.PacNomeSocial,
            ExaPacSexo: paciente.PacSexo,
            ExaUsuCodigo: user.UsuCodigo,
            ExaMedNome: medicoSolicitante?.MedNome,
            ExaMedCRM: medicoSolicitante?.MedNumeroConselho,
            ExaMedUF: medicoSolicitante?.MedUF,
            exameItens: procedimentos.map(proc => ({
              ItmExaCodigo: proc.exameAlvaro?.Codigo || '',
              ItmMaterial: proc.exameAlvaro?.Material || '',
              ItmExaQtde: 1,
            })),
          };

          setLoading(true);

          const response = await api.post('exame', criaExame);
          if (response.data.id)
            history.push({
              pathname: '../exame',
              search: qs.stringify({
                id: response.data.id,
                data: dataAgenda,
                agenda: codigoAgenda,
                somenteLista,
              }),
            });
        } catch (error) {
          const mensagem = getResponseErrors(error);
          addToast({
            type: 'error',
            title: 'Erro ao criar exame',
            description: mensagem,
          });
        } finally {
          setLoading(false);
        }
      }
    }
  }, [
    ageClinica,
    id,
    paciente,
    procedimentos,
    user.UsuCodigo,
    history,
    codigoAgenda,
    dataAgenda,
    gerouOS,
    addToast,
    somenteLista,
  ]);

  const handleAbrirExame = useCallback(() => {
    if (gerouOS.CodExame) {
      history.push({
        pathname: '../exame',
        search: qs.stringify({
          id: gerouOS.CodExame,
          data: dataAgenda,
          agenda: codigoAgenda,
          somenteLista,
        }),
      });
    }
  }, [codigoAgenda, gerouOS.CodExame, dataAgenda, somenteLista, history]);

  return (
    <Container fluid className="pl-0">
      {loading && <Loading backgroundBlack={false} />}

      <Row noGutters>
        <Col sm={0} md={3} lg={2} className="pr-2">
          <div className="bg-white border h-100">
            <h5 className="p-2 text-secondary">Prontuário</h5>
            <small className="text-info">Duração da consulta</small>
            <div
              id="duracao"
              className="alert alert-secondary border text-center text-light d-none"
              style={{ fontSize: '26px' }}
            >
              <span id="tempo">
                <FaClock size="12" />
              </span>
              <span id="numberCountdown">00:00:00</span>
            </div>
            <Button
              type="button"
              variant="success"
              className={`mb-1 ${!gerouOS.CodExame ? '' : 'd-none'}`}
              onClick={handleCriarExame}
            >
              <FaPlay size="12" />
              Iniciar Atendimento
            </Button>
            <Button
              type="button"
              variant="danger"
              className={`mb-1 ${
                !gerouOS.gerou && gerouOS.CodExame ? '' : 'd-none'
              }`}
              onClick={handleAbrirExame}
            >
              <FaPlay size="12" />
              Gerar Pedido Exame
            </Button>
            <Button
              type="button"
              variant="outline-info"
              className="mb-1"
              onClick={handleVoltar}
            >
              <FaStop size="12" /> Voltar para Agenda
            </Button>

            <div className="list-group list-group-flush small mt-1">
              Histórico
              {/* <a href="#" data-toggle="list" className="list-group-item list-group-item-action text-info active" onclick="document.getElementById('FrameAtendimento').src='Historico?PacCodigo=<%=HidPacCodigo.Value%>&Atendimento=1'; document.getElementById('tituloframe').innerText='Histórico de Atendimento'; document.getElementById('tituloframe').scrollIntoView();">Histórico</a> */}
            </div>
          </div>
        </Col>
        <Col sm={12} md={9} lg={10}>
          <Titulo className="mb-0">Paciente</Titulo>
          <div className="bg-white border rounded p-2 mt-2">
            {paciente && (
              <PacienteCabecalho
                paciente={paciente}
                pacienteID={paciente.PacCodigo}
                procedimentos={procedimentos}
                exibeEditar
                idadeCompleta
              />
            )}
          </div>
          <Row>
            <Col>
              <div className="mt-1 mb-2" style={{ minHeight: '350px' }}>
                <Row>
                  <Col md="3">
                    <Titulo>Exames a realizar</Titulo>
                  </Col>
                  <Col md="9" />
                </Row>
                <div className="w-100" />

                <div className="bg-white p-2 border rounded">
                  <table className="table table-hover table-sm border">
                    <tbody>
                      <tr>
                        <th>Procedimento Clínica</th>
                        <th>Código Alvaro</th>
                        <th>Descrição Alvaro</th>
                        <th>Dados Obrigatórios</th>
                      </tr>
                      {procedimentos &&
                        procedimentos.map(proc => (
                          <tr key={proc.ProCodigo}>
                            <td>{proc.ProNome}</td>
                            <td>{proc.ProCodigoAlvaro}</td>
                            <td>
                              {proc.exameAlvaro?.Nome || 'NÃO CADASTRADO'}
                            </td>
                            <td>{proc.exameAlvaro?.DadoObrigatorio}</td>
                          </tr>
                        ))}
                    </tbody>
                  </table>
                </div>
              </div>
            </Col>
          </Row>
        </Col>
      </Row>
    </Container>
  );
};

export default PacienteAtendimentoAlvaro;
